import { Component, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Device, ImageDevice, MediaPipelineInfo, MediaPipelineProcessInfo, SensorReadingType, TaskInfo, UserAccessRole, UserGroup } from '@models';
import { Scoutview, ScoutviewImage, SvImageFilenameDetails, ScoutviewMetadataSearchCriteria, ScoutviewExpiringSearchCriteria, ScoutviewSourceLoadCriteria } from '@models/Scoutview/scoutview.model';
import { DeviceService } from '@services/device.service';
import { UserService } from '@services/user.service';
import { Authorization } from 'app/shared/classes/authorization';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { HubConnectionState } from '@microsoft/signalr';
import { Observable, of, forkJoin } from 'rxjs';
import { from, EMPTY } from 'rxjs';
import { HubConnectionFactory } from '../../shared/services/hub-connection-factory.service';
import { Task } from '../../shared/models/Device/Task.model';
import moment from 'moment';
import { AppComponent } from 'app/app.component';
import { ScoutviewService } from '@services/scoutview.service';
import { LoadingService } from '@services/loading.service';
import _ from 'lodash';
import { GatewayImageStatsInfo, MetaDataUpdateOption, SvMetadataInfo } from '@models/Scoutview/scoutviewInfo.model';
import { CommonShared } from 'app/shared/classes/common';
import { CameraSettingsComponent, CameraSettingsParams } from './camera-settings/camera-settings.component';
//import { Lightbox, LightboxConfig } from 'ngx-lightbox';
import { ImageTag } from '@models/Scoutview/ImageTag.model';
import { DeviceMapping } from 'app/shared/classes/deviceMapping';
import { SensorService } from '@services/sensor.service';
import { MediaPipelineComponent, MediaPipelineParams } from './media-pipeline/media-pipeline.component';
import { MediaPipelineService } from '@services/mediaPipeline.service';
import { AccessTaskParams } from '@models/Task/AccessTaskParams.model';
import { DateRanges } from '@models/ranges';
import { DataShareService } from '@services/dataShare.service';
import { ImageFavoriteService } from '@services/imageFavorite.service';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteSize } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageFavoriteEditModalComponent, ImageFavoriteGateway } from 'app/shared/components/image-favorite/image-favorite-edit-modal/image-favorite-edit-modal.component';


export interface MediaGallery {
  itemImageSrc: string;
  thumbnailImageSrc: string;
  alt: string;
  title: string;
}

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'app-scoutview',
  templateUrl: './scoutview.component.html',
  styleUrls: ['./scoutview.component.scss']
})

export class ScoutviewComponent implements OnInit {

  public scoutviewContainerPrefix = "scoutview-";

  public auth = new Authorization();
  public common = new CommonShared();
  public deviceMapping = new DeviceMapping();

  modalDragOn: boolean = false;

  public imageDevices: ImageDevice[] = [];
  public imageTags: ImageTag[] = [];
  public showMediaGallery: boolean = false;
  public mediaGallery: MediaGallery[] = [];

  responsiveOptions: any[] = [
    {
        breakpoint: '1500px',
        numVisible: 5
    },
    {
        breakpoint: '1024px',
        numVisible: 3
    },
    {
        breakpoint: '768px',
        numVisible: 2
    },
    {
        breakpoint: '560px',
        numVisible: 1
    }
  ];

  public devices: Device[] = [];
  public scoutviews: Scoutview[] = [];
  public scoutview: Scoutview;
  public searchedScoutviews: Scoutview[] = [];
  public scoutviewsSelected: boolean = false;
  public sensorReadingTypes: SensorReadingType[] = [];
  public associatedUserGroups: UserGroup[] = [];
  public userGroups: UserGroup[] = [];
  public username = 'unknown';
  public allImagesSelected: boolean = false;
  public invalidDates: moment.Moment[] = [];
  public selectedDateRange: DateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
  public isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));
  public ranges = DateRanges;
  public totalScoutviewImageCount: number = 0;
  public tagsToAdd: string;
  public tagsToRemove: string;
  public selectedImageDevice: ImageDevice;

  public metadataCols = [
    {field: 'captureDate', header: 'Captured', width: '13%' },
    {field: 'expireDate', header: 'Expires', width: '13%' },
    {field: 'modifiedDate', header: 'Modified', width: '13%' },
    {field: 'sourceId', header: 'Source Identifier', width: '10%' },
    {field: 'sourceName', header: 'Source Name', width: '12%' },
    {field: 'tags', header: 'Tags', width: '11%' },
    {field: 'imageDimensions', header: 'Image Info', width: '9%' },
    {field: 'gpsLocation', header: 'GPS', width: '10%' },
    {field: 'svPipelineSequence', header: 'Pipeline Seq', width: '10%' }
  ];

  public imageDeviceCols = [
    {field: 'isSelected', header: '', width: '12%' },
    {field: 'name', header: '', width: '88%' }
  ];

  // Last-alive within this many minutes = online (green); otherwise offline (red). Adjust to taste.
  public onlineThresholdMinutes = 60;

  public imageTagCols = [
    {field: 'isSelected', header: '', width: '10%' },
    {field: 'name', header: '', width: '90%' }
  ];

  public mediaPipelineCols = [
    {field: 'name', header: 'Name', width: '15%' },
    {field: 'description', header: 'Description', width: '30%' },
    {field: 'numProcesses', header: '#Processes', width: '5%' },
    {field: 'isActive', header: 'Active', width: '5%' },
    {field: 'sourceName', header: 'Media Source', width: '15%' },
    {field: 'lastRunDate', header: 'Last Run Date', width: '15%' },
    {field: 'copy', header: '', width: '5%' },
    {field: 'edit', header: '', width: '5%' },
    {field: 'delete', header: '', width: '5%' }
  ];

  public mediaPipelineConfig = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Pipeline',
    noResultsFound: 'No pipelines available',
    searchOnKey: 'name',
    limitTo: 0
  };

  public mediaSourceConfig = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Media Source',
    noResultsFound: 'No selected media sources found!',
    searchOnKey: 'name',
    limitTo: 0
  };

  public mediaPipelineInfos: MediaPipelineInfo[] = [];
  public selectedMediaPipeline: MediaPipelineInfo;
  //public fullSizeImageRef: BsModalRef;
  public cameraSettingsRef: BsModalRef;
  public pipelinePreviewRef: BsModalRef;
  public updateMetadataTagsRef: BsModalRef;
  public viewFullScreenRef: BsModalRef;
  public albums:any = [];

  public globalSwitch: boolean;
  private hubConnection: signalR.HubConnection;

  // Fullscreen viewer state
  public showFullscreenViewer: boolean = false;
  public viewerImageSrc: string = '';
  public viewerImageLoading: boolean = false;
  public viewerZoomLevel: number = 1;
  public viewerPanX: number = 0;
  public viewerPanY: number = 0;
  private viewerIsPanning: boolean = false;
  private viewerLastX: number = 0;
  private viewerLastY: number = 0;
  public viewerBoxZoomMode: boolean = false;
  public boxZoomActive: boolean = false;
  private boxStartX: number = 0;
  private boxStartY: number = 0;
  public boxCurrentX: number = 0;
  public boxCurrentY: number = 0;


  constructor(private deviceService: DeviceService, private scoutviewService: ScoutviewService, public toastr: ToastrService,
              public modalService: BsModalService, private global: AppComponent, private confirmationService: ConfirmationService,
              public _userService: UserService, private hubFactory: HubConnectionFactory, public _mediaPipelineService: MediaPipelineService,
              public _sensorService: SensorService, private dataShareService: DataShareService,
              private loadingService: LoadingService, private imageFavoriteService: ImageFavoriteService,
              route: ActivatedRoute) {
                this.globalSwitch = global.globalSwitch;
                this.linkedImageFavoriteId = Number(route.snapshot.queryParamMap.get('imageFavoriteId')) || undefined;
                this.openImageFavoritesTab = this.linkedImageFavoriteId != undefined;
                
                this.hubConnection = this.hubFactory.create('/api/hubs/scoutview');
                this.hubConnection.onclose(error => console.log('hubConnection.onclose') );
                this.hubConnection.onreconnecting(error => console.log('Attempting reconnect...') );
                this.hubConnection.onreconnected(() => console.log('Reconnected.') );
                this.hubConnection.on('refreshScoutview', (updateType: string, updatedScoutview: string) => this.refreshScoutview(updateType, updatedScoutview));
  }


  ngOnInit(): void {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {

        this.username = results['value'];
        if (this.username != null && this.username != '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.getSensorDatatypesWithIDs();
    this.startSignalR();
    //this.getAssociatedUserGroups();
    this.startTimer4DeviceAliveCheck();
    this.getTotalScoutviewImageCount();
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  private startSignalR(): Observable<any> {
    return (this.hubConnection.state === HubConnectionState.Disconnected) ? from(this.hubConnection.start()) : EMPTY;
  }

  public ngOnDestroy() { this.stopSignalR(); }

  private stopSignalR() { this.hubConnection.stop(); }

  private refreshScoutview(updateType: string, updatedScoutview: string) {
    this.refreshScoutviewImage(updateType, updatedScoutview);
  }

  public refreshScoutviewImage(filename: string, updatedScoutview: string) {
    if (filename == undefined || filename == '') return;

    var serial = filename.split('_');
    var svDevice = this.devices.find(x => x.serialNumber == serial[0]);

    var filenameSplit = filename.split('.');

    if (filenameSplit.length > 0) {
      var ext = filenameSplit[filenameSplit.length-1];
    }
  }


  getAssociatedUserGroups(): void {
    this._userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results!=null) {
          this.associatedUserGroups = results;
          this.getUserGroups();
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  getUserGroups(): void {
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.getDevices(); 
        } else {
          this.toastr.warning('No user groups found');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving all user groups');
      });
  }


  getSensorDatatypesWithIDs(): void {
    this._sensorService.getSensorDatatypesWithIDs().subscribe(
      results => {
        if (results != null) {
          this.sensorReadingTypes = results;
          this.common.sortArrayByTypeParm(this.sensorReadingTypes);
          this.getAssociatedUserGroups();
        } else {
          this.toastr.warning('No reading types found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving reading types.');
      });
  }


  getDevices(): void {
    this.deviceService.getDevices().subscribe(
      results => {
        if (results != null) {
          this.devices = this.deviceMapping.mapDeviceInfos2Devices(results, this.sensorReadingTypes);
          //this.common.sortArrayByNameParm(this.devices);

          this.devices.forEach(d => {
            this.auth.mapUserAccessForDevice(d, this.globalSwitch, this.username, this.userGroups);
            //this.getGatewayVersionInfo(d);
          });
          this.common.sortArrayByNameParm(this.devices);
          this.buildImageDevicesList();
        }
        else {
          this.toastr.warning('No gateway devices found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving gateways.');
      });
  }


  getTotalScoutviewImageCount() {
    this.scoutviewService.getSvMetaDataCount().subscribe(
      results => {
        this.totalScoutviewImageCount = results;
      },
      err => {
      });
  }


  getMetaDataForSelectedSourceIdsAndDateRange() {
    var selectedSourceIds = this.imageDevices.filter(x => x.isSelected).map(x => x.serialNumber);

    var searchCriteria: ScoutviewMetadataSearchCriteria = {
      sourceIds: selectedSourceIds,
      startDate: this.selectedDateRange.startDate.utc().toDate(),
      endDate: this.selectedDateRange.endDate.utc().toDate()
    }
    
    this.scoutviewService.getSvMetadatasWithSearchParameters(searchCriteria).subscribe(
      results => {
        var metaDataInfos: SvMetadataInfo[] = results;
        metaDataInfos.forEach(x => {
          x.captureDate = moment.utc(x.captureDate).local().toDate();
          x.modifiedDate = moment.utc(x.modifiedDate).local().toDate();
        });

        this.createScoutviewsFromMetadata(MetaDataUpdateOption.Update, metaDataInfos);
        this.applyDeviceInfoToScoutviews();
        this.buildImageTagList();
        this.searchScoutviews();
        // Reflect the actual selection of the (re)loaded cards rather than assuming none are selected,
        // so the Select All / Unselect All toggle stays correct after a refresh.
        this.allImagesSelected = this.searchedScoutviews.length > 0 && !this.searchedScoutviews.some(x => !x.selected);
      },
      err => {
      });
  }


  buildImageTagList(applyDateFilter: boolean = true) {
    var imageTagList: string[] = [];

    this.scoutviews.forEach(x => {
      if (!applyDateFilter ||
          (x.svMetadata.captureDate >= this.selectedDateRange.startDate.toDate() && x.svMetadata.captureDate <= this.selectedDateRange.endDate.toDate()))
      {
        imageTagList = imageTagList.concat(x.svMetadata.tags);
        imageTagList.push('Hour:' + x.svMetadata.captureDate.getHours() );
      }
    });

    imageTagList = imageTagList.filter((i,p) => imageTagList.indexOf(i)===p);   // Get unique
    imageTagList = imageTagList.map(x => { return x; });
    this.common.sortStringArray(imageTagList);

    this.imageTags = [];
    imageTagList.forEach(x => {
      var imageTag: ImageTag = {
        name: x,
        isSelected: false
      }
      this.imageTags.push(imageTag);
    });
  }


  createScoutviewsFromMetadata(metadataUpdateOption: MetaDataUpdateOption, metadataInfos: SvMetadataInfo[]) {
    if (metadataUpdateOption == MetaDataUpdateOption.New) {
      this.scoutviews = [];
    }

    metadataInfos.forEach(x => {
      var scoutview: Scoutview = {
        image: this.setupSvImage(x, 'latest'),
        latestThumbnail: this.setupSvImage(x, 'latestThumbnail'),
        origThumbnail: this.setupSvImage(x, 'origThumbnail'),
        svMetadata: x,
        userName: '',
        userGroup: '',
        userGroupId: 0,
        selected: false,
        expireDays: moment(x.expireDate).diff(moment(),'days'),
        expandedMetadata: false,
        latestThumbnailIsForMP4: false,
        showOriginal: false
      }

      if (scoutview.latestThumbnail.blobName.endsWith('jpg') && scoutview.image.blobName.endsWith('mp4')) {
        scoutview.latestThumbnailIsForMP4 = true;
      }

      var existingScoutviewIndex = this.scoutviews.findIndex(y => y.svMetadata.id == x.id);

      if (metadataUpdateOption == MetaDataUpdateOption.New || existingScoutviewIndex < 0) {
        this.scoutviews.push(scoutview);
      }
      else if (metadataUpdateOption == MetaDataUpdateOption.Update && existingScoutviewIndex > -1) {
        this.scoutviews[existingScoutviewIndex].svMetadata = x;
      }
    });
  }


  setupSvImage(metadata: SvMetadataInfo, type: string): ScoutviewImage {
    var svFilenameDetails = this.getSvImageFilenameDetails(metadata);

    var retrieveFile = "";
    switch (type) {
      case "latest":
        retrieveFile = svFilenameDetails.latestFile;
        break;
      case "original":
          retrieveFile = svFilenameDetails.origFile;
          break;
      case "latestThumbnail":
        retrieveFile = svFilenameDetails.latestThumbnailFile;
        break;
      case "origThumbnail":
        retrieveFile = svFilenameDetails.origThumbnailFile;
        break;
      default:
        retrieveFile = svFilenameDetails.latestThumbnailFile;
        break;
    }

    var svImage: ScoutviewImage = {
      isLoading: false,
      blobName: retrieveFile,
      title: '',
      source: '',
      needsRefresh: false
    }
    return svImage;
  }

  
  buildImageDevicesList() {
    this.imageDevices = [];

    this.devices.forEach(x => {
      if (x.deviceCameras != undefined && x.deviceCameras.length > 0) {
        var imageDevice: ImageDevice = {
          id: x.id,
          name: x.name,
          serialNumber: x.serialNumber,
          userName: x.userName,
          userGroup: x.userGroup,
          userGroupId: x.userGroupId,
          isSelected: false,
          type: '',
          userFeatureAccess: x.userFeatureAccess,
          aliveColor: 'lightgray',
          aliveTimeStamp: null,
          imageCount: 0,
          rangeCount: 0,
          expiring3d: 0,
          expiring3w: 0,
          expiring3m: 0,
          version: x.version,
          versionInfo: x.versionInfo,
          versionWarning: x.versionWarning
        };

        if (x.userFeatureAccess['ViewDevice']) {
          this.imageDevices.push(imageDevice);
        }
      }
    });
    
    this.imageDevices = [...this.imageDevices];
    this.getDeviceAliveTimes();
    this.getGatewayImageStats();
  }


  getGatewayImageStats(): void {
    if (this.imageDevices.length === 0) {
      return;
    }

    var searchCriteria: ScoutviewMetadataSearchCriteria = {
      sourceIds: this.imageDevices.map(d => d.serialNumber),
      startDate: this.selectedDateRange.startDate.utc().toDate(),
      endDate: this.selectedDateRange.endDate.utc().toDate()
    };

    this.scoutviewService.getGatewayImageStats(searchCriteria).subscribe(
      results => {
        if (results == null) {
          return;
        }
        results.forEach(s => {
          const idx = this.imageDevices.findIndex(d => d.serialNumber === s.sourceId);
          if (idx >= 0) {
            this.imageDevices[idx].imageCount = s.totalCount;
            this.imageDevices[idx].rangeCount = s.rangeCount;
            this.imageDevices[idx].expiring3d = s.expiringIn3Days;
            this.imageDevices[idx].expiring3w = s.expiringIn3Weeks;
            this.imageDevices[idx].expiring3m = s.expiringIn3Months;
          }
        });
        this.imageDevices = [...this.imageDevices];   // trigger p-table change detection
      },
      err => {
      });
  }


  applyDeviceInfoToScoutviews() {
    this.devices.forEach(x => {
      var scoutviewsWithSourceId = this.scoutviews.filter(y => y.svMetadata.sourceId == x.serialNumber);

      scoutviewsWithSourceId.forEach(z => {
        z.svMetadata.sourceName = x.name;
      });
    });
  }


  sortSearchedScoutviews() {
    this.searchedScoutviews.sort((x, y) => {
      if (x.svMetadata.captureDate.getTime() < y.svMetadata.captureDate.getTime()) {
        return 1;
      }
      else if (x.svMetadata.captureDate.getTime() > y.svMetadata.captureDate.getTime()) {
        return -1;
      }
    });
  }


  // The gateway whose checkbox is ticked right now (only one can be), or undefined when none is. Unlike
  // selectedImageDevice, which keeps pointing at the last gateway clicked even after it is unchecked, this
  // always follows the checkbox.
  get checkedImageDevice(): ImageDevice | undefined {
    return this.imageDevices.find(x => x.isSelected);
  }


  imageSourceSelected(imageDevice: ImageDevice) {

    this.imageDevices.forEach(x => x.isSelected = (x.id == imageDevice.id && imageDevice.isSelected) ? true : false);

    this.selectedImageDevice = imageDevice;
    this.getMetaDataForSelectedSourceIdsAndDateRange();
  }


  // Loads the images for a gateway that expire within the given window (days), regardless of
  // capture date. This replaces the grid with exactly the expiring set (bypasses the date-range
  // filter in searchScoutviews); changing the date range or a tag reverts to the normal search.
  loadExpiringImagesForGateway(gateway: ImageDevice, days: number): void {
    var expiringSearchCriteria: ScoutviewExpiringSearchCriteria = {
      sourceId: gateway.serialNumber,
      days: days
    };

    this.scoutviewService.getExpiringSvMetadatas(expiringSearchCriteria).subscribe(
      results => {
        this.applyLoadedMetadataToGrid(results);
      },
      err => {
      });
  }


  // First "(#/#)" number clicked: behaves exactly like checking the gateway's box —
  // selects the gateway and loads its images for the current date range.
  onGatewayRangeCountClicked(gateway: ImageDevice): void {
    gateway.isSelected = true;
    this.imageSourceSelected(gateway);
  }


  // Second "(#/#)" number clicked: selects the gateway and loads its all-time images.
  // If there are more than 100, ask whether to load the newest 100 or the oldest 100.
  onGatewayTotalCountClicked(gateway: ImageDevice): void {
    this.selectGatewayOnly(gateway);

    if (gateway.imageCount <= 100) {
      this.loadGatewayImages(gateway, true, 0);   // load all
      return;
    }

    this.confirmationService.confirm({
      key: 'cdLoadCount',
      header: 'Load Images',
      message: `${gateway.name} has ${gateway.imageCount} images. Load the newest 100 or the oldest 100?`,
      acceptLabel: 'Newest 100',
      rejectLabel: 'Oldest 100',
      accept: () => {
        this.loadGatewayImages(gateway, true, 100);
      },
      reject: (type: ConfirmEventType) => {
        if (type === ConfirmEventType.REJECT) {
          this.loadGatewayImages(gateway, false, 100);
        }
        // ConfirmEventType.CANCEL (dialog closed / escaped) => do nothing
      }
    });
  }


  // Marks a gateway as the selected one (single-select, like the checkbox) without triggering
  // the date-range metadata load — used when we are about to load a different image set.
  selectGatewayOnly(gateway: ImageDevice): void {
    this.imageDevices.forEach(x => x.isSelected = (x.id == gateway.id));
    this.selectedImageDevice = gateway;
  }


  // Loads a gateway's images (all-time), ordered newest/oldest by capture date, optionally limited
  // to `take` items (0 = all). Replaces the grid, bypassing the date-range filter in searchScoutviews.
  loadGatewayImages(gateway: ImageDevice, newest: boolean, take: number): void {
    var sourceLoadCriteria: ScoutviewSourceLoadCriteria = {
      sourceId: gateway.serialNumber,
      newest: newest,
      take: take
    };

    this.scoutviewService.getSvMetadatasForSource(sourceLoadCriteria).subscribe(
      results => {
        this.applyLoadedMetadataToGrid(results);
      },
      err => {
      });
  }


  // Applies a freshly loaded metadata set directly to the displayed grid, bypassing the
  // capture-date/tag filtering in searchScoutviews (used by the expire and total-count drill-downs).
  private applyLoadedMetadataToGrid(results: SvMetadataInfo[]): void {
    if (results == null) {
      results = [];
    }
    results.forEach(x => {
      x.captureDate = moment.utc(x.captureDate).local().toDate();
      x.modifiedDate = moment.utc(x.modifiedDate).local().toDate();
    });

    this.createScoutviewsFromMetadata(MetaDataUpdateOption.New, results);   // resets this.scoutviews
    this.applyDeviceInfoToScoutviews();
    this.buildImageTagList(false);   // build tags from the loaded set, ignoring the date range
    this.searchedScoutviews = [...this.scoutviews];
    this.sortSearchedScoutviews();
    this.scoutviewsSelected = false;
    this.allImagesSelected = false;
    this.albums = [];
  }


  onImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    const serialNumber = this.selectedImageDevice.serialNumber;
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const dateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const sanitizedName = file.name.replace(/\s+/g, '');
    const renamedFileName = `${serialNumber}_${dateTime}_${sanitizedName}`;

    this.scoutviewService.uploadImage(file, renamedFileName).subscribe({
      next: () => {
        this.toastr.success('Image uploaded successfully.');
        this.refreshImageList();
      },
      error: (err) => {
        this.toastr.error('Failed to upload image.');
        console.error(err);
      }
    });

    input.value = '';
  }


  imageTagsFiltered() {
    this.searchScoutviews();
  }


  searchScoutviews() {
    this.searchedScoutviews = [];

    // Selected by date
    var searchResultsByDate = this.scoutviews.filter(x => moment(x.svMetadata.captureDate).isAfter(this.selectedDateRange.startDate.toDate()) &&
                                                          moment(x.svMetadata.captureDate).isBefore(this.selectedDateRange.endDate.toDate()) );

    // Selected by Source
    var selectedImageDevices = this.imageDevices.filter(x => x.isSelected).map(y => y.name);
    this.searchedScoutviews = searchResultsByDate.filter(x => selectedImageDevices.includes(x.svMetadata.sourceName));

    // Selected by Tags
    var searchResultsByTags: Scoutview[] = [];
    var selectedImageTags = this.imageTags.filter(x => x.isSelected).map(y => y.name);

    if (selectedImageTags.length > 0) {
      this.searchedScoutviews.forEach(x => {
        if (selectedImageTags.every(r => x.svMetadata.tags.includes(r))) { 
          searchResultsByTags.push(x);
        }
      });
    }
    else {
      searchResultsByTags = this.searchedScoutviews;
    }

    // Find common elements across the three filtered results
    var data = [searchResultsByTags];
    this.searchedScoutviews = data.reduce((a, b) => a.filter(c => b.includes(c)));

    this.sortSearchedScoutviews();

    this.scoutviewsSelected = this.searchedScoutviews.some(x => x.selected === true);
    this.albums = [];

    // Load Pipeline Info
    if (selectedImageDevices.length > 0) {
      this.getMediaPipelines();
    }
    else {
      this.mediaPipelineInfos = [];
    }
  }


  showHeader() {
    this.dataShareService.hideGlobalHeader(false);
  }


  showMedia(scoutview: Scoutview, svImage: ScoutviewImage, forceLoad: boolean, hideHeader: boolean) {
    this.dataShareService.hideGlobalHeader(hideHeader);
    this.downloadSvImageForViewing(scoutview, svImage, forceLoad, undefined);
  }


  openFullscreenViewer(scoutview: Scoutview, imageVersion: 'latest' | 'original' = 'latest') {
    this.dataShareService.hideGlobalHeader(true);
    this.showFullscreenViewer = true;
    this.viewerZoomLevel = 1;
    this.viewerPanX = 0;
    this.viewerPanY = 0;

    const blobName = imageVersion === 'original'
      ? this.getSvImageFilenameDetails(scoutview.svMetadata).origFile
      : scoutview.image.blobName;

    if (imageVersion === 'latest' && scoutview.image.source) {
      this.viewerImageSrc = scoutview.image.source;
      this.viewerImageLoading = false;
    } else {
      this.viewerImageLoading = true;
      this.viewerImageSrc = '';
      this.scoutviewService.getSvImageAsBase64(scoutview.svMetadata.sourceId, blobName, '').subscribe(
        results => {
          const dataUri = 'data:image/jpeg;base64,' + results;
          if (imageVersion === 'latest') {
            scoutview.image.source = dataUri;
          }
          this.viewerImageSrc = dataUri;
          scoutview.image.isLoading = false;
          this.viewerImageLoading = false;
        },
        err => {
          this.viewerImageLoading = false;
          this.toastr.error('Error loading full-size image');
        });
    }
  }

  closeFullscreenViewer() {
    this.showFullscreenViewer = false;
    this.viewerImageSrc = '';
    this.dataShareService.hideGlobalHeader(false);
  }

  viewerZoomIn() {
    this.viewerZoomLevel = Math.min(10, this.viewerZoomLevel + 0.25);
  }

  viewerZoomOut() {
    this.viewerZoomLevel = Math.max(0.25, this.viewerZoomLevel - 0.25);
    if (this.viewerZoomLevel <= 1) {
      this.viewerPanX = 0;
      this.viewerPanY = 0;
    }
  }

  viewerResetView() {
    this.viewerZoomLevel = 1;
    this.viewerPanX = 0;
    this.viewerPanY = 0;
    this.viewerBoxZoomMode = false;
    this.boxZoomActive = false;
  }

  toggleBoxZoomMode() {
    this.viewerBoxZoomMode = !this.viewerBoxZoomMode;
    this.boxZoomActive = false;
  }

  getBoxRect(): { left: number; top: number; width: number; height: number } {
    const left = Math.min(this.boxStartX, this.boxCurrentX);
    const top = Math.min(this.boxStartY, this.boxCurrentY);
    const width = Math.abs(this.boxCurrentX - this.boxStartX);
    const height = Math.abs(this.boxCurrentY - this.boxStartY);
    return { left, top, width, height };
  }

  onViewerWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.15 : 0.15;
    this.viewerZoomLevel = Math.min(10, Math.max(0.25, this.viewerZoomLevel + delta));
    if (this.viewerZoomLevel <= 1) {
      this.viewerPanX = 0;
      this.viewerPanY = 0;
    }
  }

  onViewerMouseDown(event: MouseEvent) {
    if (this.viewerBoxZoomMode) {
      event.preventDefault();
      this.boxZoomActive = true;
      this.boxStartX = event.clientX;
      this.boxStartY = event.clientY;
      this.boxCurrentX = event.clientX;
      this.boxCurrentY = event.clientY;
      return;
    }
    if (this.viewerZoomLevel > 1) {
      event.preventDefault();
      this.viewerIsPanning = true;
      this.viewerLastX = event.clientX;
      this.viewerLastY = event.clientY;
    }
  }

  onViewerMouseMove(event: MouseEvent) {
    if (this.boxZoomActive) {
      this.boxCurrentX = event.clientX;
      this.boxCurrentY = event.clientY;
      return;
    }
    if (!this.viewerIsPanning) return;
    const dx = event.clientX - this.viewerLastX;
    const dy = event.clientY - this.viewerLastY;
    this.viewerPanX += dx / this.viewerZoomLevel;
    this.viewerPanY += dy / this.viewerZoomLevel;
    this.viewerLastX = event.clientX;
    this.viewerLastY = event.clientY;
  }

  onViewerMouseUp() {
    if (this.boxZoomActive) {
      this.boxZoomActive = false;
      this.applyBoxZoom();
      return;
    }
    this.viewerIsPanning = false;
  }

  private applyBoxZoom() {
    const rect = this.getBoxRect();
    if (rect.width < 20 || rect.height < 20) return;

    const imgEl = document.querySelector('.fullscreen-viewer-image') as HTMLElement;
    const containerEl = document.querySelector('.fullscreen-viewer-container') as HTMLElement;
    if (!imgEl || !containerEl) return;

    const imgRect = imgEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    // Convert box screen coords to image-space coords (accounting for current zoom & pan)
    const imgCenterX = imgRect.left + imgRect.width / 2;
    const imgCenterY = imgRect.top + imgRect.height / 2;

    const boxCenterScreenX = rect.left + rect.width / 2;
    const boxCenterScreenY = rect.top + rect.height / 2;

    // Box center offset from image center in screen pixels, then convert to image space
    const offsetX = (boxCenterScreenX - imgCenterX) / this.viewerZoomLevel;
    const offsetY = (boxCenterScreenY - imgCenterY) / this.viewerZoomLevel;

    // Box dimensions in image space
    const boxWidthImg = rect.width / this.viewerZoomLevel;
    const boxHeightImg = rect.height / this.viewerZoomLevel;

    // New zoom to fill the container with the selected region
    const newZoom = Math.min(
      10,
      Math.max(0.25,
        Math.min(containerRect.width / boxWidthImg, containerRect.height / boxHeightImg)
      )
    );

    // New pan: shift so the box center becomes the viewport center
    this.viewerPanX = this.viewerPanX - offsetX;
    this.viewerPanY = this.viewerPanY - offsetY;
    this.viewerZoomLevel = newZoom;

    this.viewerBoxZoomMode = false;
  }


  downloadSvImageForViewing(scoutview: Scoutview, svImage: ScoutviewImage, forceLoad: boolean, mediaGallery: MediaGallery) {
    if (svImage.source != '' && !forceLoad) {     // Image already loaded into memory. Don't load again unless forced to
      return;   
    }

    svImage.isLoading = true;

    this.scoutviewService.getSvImageAsBase64(scoutview.svMetadata.sourceId, svImage.blobName, '').subscribe(
      results => {
        if (svImage.blobName.endsWith(".jpg")) {
          svImage.source = "data:image/jpeg;base64," + results;
        }
        else if (svImage.blobName.endsWith(".mp4")) {
          svImage.source = "data:video/mp4;base64," + results;
        }
        else if (svImage.blobName.endsWith(".webm")) {
          svImage.source = "data:video/webm;base64," + results;
        }

        if (mediaGallery != undefined) {
          mediaGallery.itemImageSrc = svImage.source;
        }

        svImage.isLoading = false;
        svImage.title = '';
      },
      err => {
        svImage.isLoading = false;
        svImage.title = '';
      });
  }


  downloadSvImage(sourceId: string, image: ScoutviewImage) {
    this.scoutviewService.getSvImage(sourceId, image.blobName, '').subscribe(
      results => {
        this.toastr.success('Downloaded ' + image.blobName);
        this.saveImage(results, image.blobName);
      },
      err => {
      });
  }


  openSelectedImagesInGallery() {
    var selectedsvImages = this.searchedScoutviews.filter(x => x.selected == true);

    this.mediaGallery = [];

    selectedsvImages.forEach(x => {
      var newImageSet: MediaGallery = {
        itemImageSrc: x.image.source,
        thumbnailImageSrc: x.latestThumbnail.source,
        alt: '',
        title: x.image.title
      };

      this.downloadSvImageForViewing(x, x.image, false, newImageSet);
      this.mediaGallery.push(newImageSet);
    });

    this.dataShareService.hideGlobalHeader(true);
    this.showMediaGallery = true;
  }


  mediaGalleryVisibilityChanged(value: boolean) {
    this.showMediaGallery = value;
    this.dataShareService.hideGlobalHeader(value);
  }


  saveImage(dataString, fileName: string) {
    const blob = new Blob([dataString], { type: 'mime' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


  buildNewTask(deviceID: number, action: string, payload: string): Task {
    const task: Task = {
      taskID: -1,
      deviceID,
      clientID: this.common.generateUUID(),
      priority: 1,
      type: 1,
      action,
      payload,
      expireTimeSec: 87,
      taskWaitIntervalSec: 3,
      waitForResult: true
    };

    return task;
  }


  onDeleteImageConfirm(scoutview: Scoutview) {
    this.scoutviewService.deleteSvImagesAndMetadata(scoutview.svMetadata.sourceId, scoutview.svMetadata.fileName).subscribe(
      results => {
        if (results == undefined) {
          this.toastr.success('Media deleted');

          var svIndex = this.scoutviews.findIndex(x => x.svMetadata.fileName == scoutview.svMetadata.fileName);

          if (svIndex > -1) {
            this.scoutviews.splice(svIndex, 1);
            this.searchScoutviews();
          }
        }
      },
      err => {
        this.toastr.error("Error occourred while attempting to delete the media.")
      });
  }


  datesUpdated(quickPick: string): void {
    if (quickPick === 'Today') {
      this.selectedDateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 2 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(1, 'days'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 7 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(6, 'days'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 30 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(29, 'days'), endDate: moment().endOf('day') };
    }

    this.getMetaDataForSelectedSourceIdsAndDateRange();
    this.getGatewayImageStats();   // refresh the per-gateway in-range count for the new range
  }


  getSvImageFilenameDetails(metadata: SvMetadataInfo): SvImageFilenameDetails {
      var filenameDetails: SvImageFilenameDetails = {
        sourceId: '',
        dateTimeString: '',
        createdDate: undefined,
        fileTag: '',
        latestFile: '',
        origFile: '',
        latestThumbnailFile: '',
        origThumbnailFile: '',
        fileExtension: '',
        isValid: false,
        errorMessage: ''
      }

      var fileName = metadata.fileName;

      if (fileName == undefined || fileName == '')
      {
          filenameDetails.errorMessage = "No filename given";
          return filenameDetails;
      }

      if (!fileName.includes("."))
      {
          filenameDetails.errorMessage = "Filename does not have an extension";
          return filenameDetails;
      }

      var fileNamePrefix = fileName.split('.')[0];
      var fileNameExtension = fileName.split('.')[1];
      var fileNamePrefixParts = fileNamePrefix.split('_');

      if (fileNamePrefixParts.length < 3)
      {
          filenameDetails.errorMessage = "Invalid filename: Filename must consist of three parts separated by '_' (i.e., <identifier>_<dateTime>_<OrigTag>.extension). Ex: 394837239_20220809T103000_Field4.jpg";
          return filenameDetails;
      }

      filenameDetails.sourceId = fileNamePrefixParts[0];
      filenameDetails.dateTimeString = fileNamePrefixParts[1];
      filenameDetails.fileTag = fileNamePrefixParts[2];

      filenameDetails.createdDate = moment.utc(filenameDetails.dateTimeString).local().toDate();

      filenameDetails.origFile = fileNamePrefixParts[0] + "_" + fileNamePrefixParts[1] + "_" + fileNamePrefixParts[2] + "." + fileNameExtension;
      filenameDetails.latestFile = fileNamePrefixParts[0] + "_" + fileNamePrefixParts[1] + "_" + fileNamePrefixParts[2] + "_latest." + fileNameExtension;

      var thumbnailExtension = (metadata != undefined && metadata.thumbnailType != undefined && metadata.thumbnailType != '') ? metadata.thumbnailType : fileNameExtension;

      filenameDetails.origThumbnailFile = fileNamePrefixParts[0] + "_" + fileNamePrefixParts[1] + "_" + fileNamePrefixParts[2] + "_origThumbnail." + thumbnailExtension;
      filenameDetails.latestThumbnailFile = fileNamePrefixParts[0] + "_" + fileNamePrefixParts[1] + "_" + fileNamePrefixParts[2] + "_latestThumbnail." + thumbnailExtension;

      filenameDetails.isValid = true;

      return filenameDetails;
  }


  openCameraSettingsControls(selectedDeviceName: string): void {
    var selectedDevice = this.devices.find(x => x.name == selectedDeviceName);
    selectedDevice = (selectedDevice != undefined) ? selectedDevice : this.devices.find(x => x.id == this.imageDevices[0].id);

    const params: CameraSettingsParams = {
      selectedDevice: selectedDevice,
      scoutviews: this.scoutviews,
      devices: this.devices,
      imageDevices: this.imageDevices,
      associatedUserGroups: this.associatedUserGroups,
      userGroups: this.userGroups,
      sensorReadingTypes: this.sensorReadingTypes,
      refreshImages: this.refreshImageList.bind(this)
    };

    this.modalService.show(CameraSettingsComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, initialState: params })
                              .onHide
                              .subscribe(() => this.refreshImageList());
  }


  selectScoutview(scoutview: Scoutview) {
    scoutview.selected = !scoutview.selected;
    this.scoutviewsSelected = this.searchedScoutviews.some(x => x.selected === true);

    this.allImagesSelected = (this.searchedScoutviews.filter(x => x.selected == false).length > 0) ? false : true;
  }


  selectAllDisplayedScoutviews() {
    this.searchedScoutviews.forEach(x => x.selected = true);
    this.scoutviewsSelected = this.searchedScoutviews.some(x => x.selected === true);
    this.allImagesSelected = true;
  }


  unSelectAllDisplayedScoutviews() {
    this.searchedScoutviews.forEach(x => x.selected = false);
    this.scoutviewsSelected = this.searchedScoutviews.some(x => x.selected === true);
    this.allImagesSelected = false;
  }


  downloadSelectedImages() {
    this.searchedScoutviews.forEach(x => {
      if (x.selected) {
        this.downloadSvImage(x.svMetadata.sourceId, x.image);
      }
    });
  }


  expandMetadataView(scoutview: Scoutview) {
    scoutview.expandedMetadata = !scoutview.expandedMetadata;
  }

  toggleImageView(scoutview: Scoutview) {
    scoutview.showOriginal = !scoutview.showOriginal;
    if (scoutview.showOriginal) {
      this.downloadSvImageForViewing(scoutview, scoutview.origThumbnail, false, undefined);
    }
  }


  refreshImageList() {
    this.getMetaDataForSelectedSourceIdsAndDateRange();
    this.getGatewayImageStats();   // refresh per-gateway counts and expiration stats
  }


  deleteSelectedScoutviews(): void {
    this.confirmationService.confirm({
      message: 'Do you want to delete all selected images and videos?',
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      key: 'cd1',
      accept: () => {
        this.searchedScoutviews.forEach(x => {
          if (x.selected) {
            this.onDeleteImageConfirm(x);
          }
        });
      },
      reject: () => {
      }
    });
  }


  adjustExpirationDate(scoutview: Scoutview, days: number) {
    var origExpirationDate = moment(scoutview.svMetadata.expireDate).toString();
    scoutview.svMetadata.expireDate = moment(scoutview.svMetadata.expireDate).add(days, 'days').toDate();

    this.scoutviewService.updateSvMetadata(scoutview.svMetadata).subscribe(
      results => {
        if (results == "success") {
          this.toastr.success('Expiration date updated');

          scoutview.expireDays = moment(scoutview.svMetadata.expireDate).local().diff(moment(),'days');

          this.searchScoutviews();
        }
        else {
          this.toastr.success('Unable to update: ' + results);
          scoutview.svMetadata.expireDate = moment(origExpirationDate).toDate();
        }
      },
      err => {
        this.toastr.error("Error occourred while attempting to update the expiration date.")
        scoutview.svMetadata.expireDate = moment(origExpirationDate).toDate();
      });
  }


  // Bulk equivalent of adjustExpirationDate: shifts the expiry of every selected image by `days`.
  // Decreases clamp to "expires today" (0 days) when an image has fewer days left than the amount.
  adjustExpirationDateForSelected(days: number) {
    const selected = this.searchedScoutviews.filter(x => x.selected);
    if (selected.length === 0) {
      return;
    }

    const loadingMsg = 'Updating expiration for selected images ...';
    this.loadingService.setMessage(loadingMsg);

    const updates: Observable<any>[] = [];

    selected.forEach(x => {
      var newExpire = moment(x.svMetadata.expireDate).add(days, 'days');

      if (days < 0 && newExpire.isBefore(moment())) {
        newExpire = moment();   // clamp to now (expires today) instead of a past date
      }

      x.svMetadata.expireDate = newExpire.toDate();
      updates.push(this.scoutviewService.updateSvMetadata(x.svMetadata));
    });

    forkJoin(updates).subscribe(
      () => {
        this.toastr.success(`Expiration updated for ${selected.length} image(s)`);
        // Re-fetch the images and gateway stats (counts + 3d/3w/3m) so the view reflects the new dates.
        this.refreshImageList();
        this.loadingService.clearMessage(loadingMsg);
      },
      err => {
        this.loadingService.clearMessage(loadingMsg);
        this.toastr.error('Error occurred while updating expiration for selected images.');
      });
  }


  updateMetadataTagsForSelectedMedia() {
    this.updateMetadataTagsRef.hide();

    if (this.tagsToAdd == '' && this.tagsToRemove == '') {
      this.toastr.warning(`Nothing to update`);
      return;
    }

    this.searchedScoutviews.forEach(x => {
      if (x.selected) {
        var newMetadata: SvMetadataInfo = x.svMetadata;

        if (this.tagsToAdd != undefined && this.tagsToAdd.length > 0) {
          var tags = this.tagsToAdd.split(',');
          if (tags.length > 0) {
            tags.forEach(t => {
              var index = newMetadata.tags.findIndex(x => x.toLowerCase() == t.trim().toLowerCase());
              if (index < 0 && t.trim().length > 0) {
                newMetadata.tags.push(t.trim());
              }
          });
          }
        }
        
        if (this.tagsToRemove != undefined && this.tagsToRemove.length > 0) {
          tags = this.tagsToRemove.split(',');
          if (tags.length > 0) {
            tags.forEach(t => {
              var index = newMetadata.tags.findIndex(x => x.toLowerCase() == t.trim().toLowerCase());
              if (index > -1) {
                newMetadata.tags.splice(index, 1);
              }
            });
          }
        }
        
        this.scoutviewService.updateSvMetadata(newMetadata).subscribe(
          results => {
            if (results == "success") {
              this.toastr.success(`Tags update for ${newMetadata.fileName}`);
            }
            else {
              this.toastr.warning(`Warning, unable to update tags for ${newMetadata.fileName}`);
            }
          },
          err => {
            this.toastr.error("Error occourred while attempting to update the tags")
          });
      }
    });
  }


  getToolTipText(tags: string[]): string {
    return tags.join(", ");
  }


  getToolTipForNumProcessesText(mediaPipeline: MediaPipelineProcessInfo): string {
    return "todo";
  }


  getMediaPipelines() {
    var selectedImageDeviceIds = this.imageDevices.filter(x => x.isSelected).map(y => y.serialNumber);

    this._mediaPipelineService.searchMediaPipelines(selectedImageDeviceIds).subscribe(
      results => {
        if (results != undefined) {
          this.mediaPipelineInfos = results;

          this.mediaPipelineInfos.forEach(x => {
            x.numProcesses = x.mediaPipelineProcesses.length;
            x.sourceName = this.imageDevices.find(y => y.serialNumber == x.sourceSerial).name;
            x.processesAsText = this.buildPipelineProcessParametersText(x);
          });
        }
      },
      err => {
        this.toastr.error('Error unable to get pipeline processes for selected sources');
      });
  }


  buildPipelineProcessParametersText(pipelineInfo: MediaPipelineInfo): string {
    var results = "";

    pipelineInfo.mediaPipelineProcesses.forEach(x => {
      results += x.mediaProcessId.toString();

      for (let key in x.parameters) {
        let value = x.parameters[key];
        results += ", " + key + "=" + value;
      }

      results += "\n";
    });

    return results;
  }


  updatePipelineIsActive(mediaPipelineInfo: MediaPipelineInfo, isActive: boolean) {
    mediaPipelineInfo.isActive = isActive;

    this._mediaPipelineService.createOrUpdateMediaPipeline(mediaPipelineInfo).subscribe(
      results => {
        this.toastr.success('Pipeline updated');
      },
      err => {
        mediaPipelineInfo.isActive = !isActive;
        this.toastr.error('Error unable to update pipeline');
      });
  }


  copyPipeline(pipeline: MediaPipelineInfo) {
    let copyIndex: number;
    for (copyIndex = 1; copyIndex < 500; copyIndex++) {
      const results = this.mediaPipelineInfos.findIndex(t => t.name == pipeline.name + ' (copy ' + copyIndex + ')');
      if (results == -1) {
        break;
      }
    }

    const newPipeline = Object.assign({}, pipeline);
    newPipeline.id = 0;
    newPipeline.isActive = false;
    newPipeline.name = pipeline.name + ' (copy ' + copyIndex + ')';

    this.createCopyPipeline(newPipeline);
  }


  editPipeline(mediaPipelineInfo: MediaPipelineInfo) {
    var selectedImageDevice = this.imageDevices.find(x => x.name == mediaPipelineInfo.sourceName);
    selectedImageDevice = selectedImageDevice ?? this.checkedImageDevice ?? this.imageDevices[0];

    const params: MediaPipelineParams = {
      selectedImageDevice: selectedImageDevice,
      scoutviews: this.scoutviews,
      imageDevices: this.imageDevices.filter(x => x.isSelected),
      mediaPipelineInfo: JSON.parse(JSON.stringify(mediaPipelineInfo)),
      createOrUpdateTag: "Update"
    };

    this.modalService.show(MediaPipelineComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, initialState: params })
    .onHide
    .subscribe(() => this.searchScoutviews());
  }


  createPipeline() {
    // The new pipeline belongs to the gateway that is checked; the button is disabled when none is.
    const selectedImageDevice = this.checkedImageDevice;
    if (selectedImageDevice == undefined) {
      return;
    }

    var mediaPipelineInfo = this.createNewMediaPipelineInfo();
    mediaPipelineInfo.sourceName = selectedImageDevice.name;
    mediaPipelineInfo.sourceSerial = selectedImageDevice.serialNumber;

    const params: MediaPipelineParams = {
      selectedImageDevice: selectedImageDevice,
      scoutviews: this.scoutviews,
      imageDevices: this.imageDevices.filter(x => x.isSelected),
      mediaPipelineInfo: mediaPipelineInfo,
      createOrUpdateTag: "Create"
    };

    this.modalService.show(MediaPipelineComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, initialState: params })
      .onHide.subscribe(() => this.searchScoutviews());
  }


  onDeleteConfirm(pipeline: MediaPipelineInfo): void {
    this.confirmationService.confirm({
      message: 'Do you want to delete this pipeline?',
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      key: 'cd1',
      accept: () => { this.deletePipeline(pipeline); },
      reject: () => { }
    });
  }


  deletePipeline(pipeline: MediaPipelineInfo) {
    this._mediaPipelineService.deleteMediaPipeline(pipeline.id).subscribe(
      results => {
        this.searchScoutviews();  // TODO. Alternative is to just remove deleted element from the local array
        this.toastr.success('Pipeline deleted');
      },
      err => {
        this.toastr.error('Error unable to delete pipeline');
      });
  }


  createCopyPipeline(mediaPipelineInfo: MediaPipelineInfo) {
    this._mediaPipelineService.createOrUpdateMediaPipeline(mediaPipelineInfo).subscribe(
      results => {
        this.searchScoutviews();  
        this.toastr.success('Pipeline copied');
      },
      err => {
        this.toastr.error('Error unable to copy pipeline');
      });
  }


  createNewMediaPipelineInfo(): MediaPipelineInfo {
    var mediaPipelineInfo: MediaPipelineInfo = {
      id: 0,
      name: 'new',
      description: '',
      mediaPipelineProcesses: [],
      isActive: false,
      sourceSerial: '',
      lastRunDate: undefined,
      numProcesses: 0,
      sourceName: '',
      processesAsText: ''
    }

    return mediaPipelineInfo;
  }


  runPipelinePreviewer(scoutview: Scoutview, template: TemplateRef<any>) {
    this.scoutview = scoutview;
    this.pipelinePreviewRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
    this.pipelinePreviewRef.onHide.subscribe(() => this.dataShareService.hideGlobalHeader(false));
  }


  runSelectedPipeline() {
    this._mediaPipelineService.applyPipelineToMedia(this.selectedMediaPipeline.id, this.scoutview.svMetadata.id).subscribe(
      results => {
        this.scoutview.pipelineResults = results;
        this.downloadSvImageForViewing(this.scoutview, this.scoutview.latestThumbnail, true, undefined);
        this.toastr.success('Pipeline completed');
      },
      err => {
        this.toastr.error('Error unable to complete pipeline');
      });
  }


  startTimer4DeviceAliveCheck() {
    setInterval(() => {
      this.getDeviceAliveTimes();
    },45000);
  }


  getDeviceAliveTimes(): void {
    const accessTaskParams: AccessTaskParams = {
      deviceSerialNumber: 'all',
      taskType: 2
    };
    this.deviceService.getTasks(accessTaskParams).subscribe(
      results => {
        if (results != null) {
          if (results.length > 0) {
            const tasks: TaskInfo[] = results;
            tasks.forEach( t => {
              const id = this.imageDevices.findIndex( d => d.id == t.deviceID);
              if (id >= 0) {
                this.imageDevices[id].aliveTimeStamp = moment.utc(t.lastModifiedDate).toDate();
                this.imageDevices[id].aliveColor = this.getDeviceAliveColor(this.imageDevices[id].aliveTimeStamp);
              }
            });
          }
        } else {
        //this.toastr.warning('No device alive timestamps found.');
        }
        return;
      },
      err => {
      //this.toastr.error('Error retrieving alive tasks from cloud.');
      });
  }


  getDeviceAliveColor(timeStamp: Date): string {
    if (timeStamp == null) {
      return 'lightgray';   // no alive data yet
    }

    const duration = moment.duration(moment().diff(this.getFormattedDate(timeStamp)));
    const diffMinutes = Math.abs(duration.asMinutes());

    // Binary online/offline: reported within the threshold = online (green), otherwise offline (red).
    return diffMinutes <= this.onlineThresholdMinutes ? '#a9d18e' : '#FF0000';
  }


  getFormattedDate(date: Date): string{
    const utcdate = moment.utc(date).format('YYYY-MM-DD HH:mm');
    const stillUtc = moment.utc(utcdate).toDate();
    const local = moment(stillUtc).local().format('YYYY-MM-DD HH:mm');
    return local;
  }


  updateMetadataTags(template: TemplateRef<any>) {
    this.tagsToAdd = '';
    this.tagsToRemove = '';
    this.updateMetadataTagsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  choosePipelineToRunOnSelectedMedia(template: TemplateRef<any>) {
    this.updateMetadataTagsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
    this.updateMetadataTagsRef.onHide.subscribe(() => this.dataShareService.hideGlobalHeader(false));
  }


  runPipelineOnSelectedMedia() {
    this.searchedScoutviews.forEach(x => {

      if (x.selected) {
        this._mediaPipelineService.applyPipelineToMedia(this.selectedMediaPipeline.id, x.svMetadata.id).subscribe(
          results => {
            x.pipelineResults = results;
            this.toastr.success('Pipeline completed for ' + x.image.blobName);
          },
          err => {
            this.toastr.error('Error unable to complete pipeline process for: ' + x.image.blobName);
          });
      }
    });
  }

  // ----- Image Favorites tab -----

  // Selected favorites and tile size are persisted to localStorage (clearable
  // from the home page's "Clear Saved Page Settings").
  private static readonly IMAGE_FAVORITES_LAYOUT_STORAGE_KEY = 'ScoutviewImageFavoritesLayout';
  private static readonly IMAGE_FAVORITES_SELECTED_STORAGE_KEY = 'ScoutviewImageFavoritesSelected';

  public imageFavorites: ImageFavoriteInfo[] = [];
  public selectedImageFavoriteIds: number[] = [];
  public imageFavoritesSize: ImageFavoriteSize = 'Medium';
  public imageFavoritesTabActive = false;
  private imageFavoritesLoaded = false;
  // Only one favorite at a time may be in label-edit mode.
  public labelEditingImageFavoriteId: number | null = null;
  public imageFavoriteEditRef: BsModalRef;

  get selectedImageFavorites(): ImageFavoriteInfo[] {
    return this.selectedImageFavoriteIds
      .map(id => this.imageFavorites.find(f => f.id === id))
      .filter(f => f != undefined);
  }


  // A link (?imageFavoriteId=, e.g. from the home page search) opens the Image Favorites tab with that
  // favorite added to the displayed ones.
  public linkedImageFavoriteId: number | undefined;
  public openImageFavoritesTab = false;


  onImageFavoritesTabSelected(): void {
    this.imageFavoritesTabActive = true;
    if (!this.imageFavoritesLoaded) {
      const savedSize = localStorage.getItem(ScoutviewComponent.IMAGE_FAVORITES_LAYOUT_STORAGE_KEY);
      if (savedSize === 'Small' || savedSize === 'Medium' || savedSize === 'Large') {
        this.imageFavoritesSize = savedSize;
      }
      const linkedId = this.linkedImageFavoriteId;
      this.linkedImageFavoriteId = undefined;
      this.getImageFavorites(linkedId);
    }
  }


  // Tiles are only rendered while the tab is active so hidden favorites stop refreshing.
  onImageFavoritesTabDeselected(): void {
    this.imageFavoritesTabActive = false;
    this.labelEditingImageFavoriteId = null;
  }


  getImageFavorites(selectId?: number): void {
    this.imageFavoriteService.getImageFavorites().subscribe(
      (results: ImageFavoriteInfo[] | null) => {
        const favorites = results ?? [];
        favorites.forEach(f => this.auth.mapUserAccessForImageFavorite(f, this.globalSwitch, this.username, this.userGroups));
        this.imageFavorites = favorites.filter(f => f.userFeatureAccess['ViewListing']);
        this.common.sortArrayByNameParm(this.imageFavorites);

        if (!this.imageFavoritesLoaded) {
          this.imageFavoritesLoaded = true;
          this.selectedImageFavoriteIds = this.restoreSelectedImageFavoriteIds();
        }
        if (selectId != undefined && !this.selectedImageFavoriteIds.includes(selectId)) {
          this.selectedImageFavoriteIds = [...this.selectedImageFavoriteIds, selectId];
        }
        this.selectedImageFavoriteIds = this.selectedImageFavoriteIds.filter(id => this.imageFavorites.some(f => f.id === id));
        this.onImageFavoriteSelectionChange();
      },
      err => {
        this.toastr.error('Error occurred while retrieving image favorites');
      });
  }


  private restoreSelectedImageFavoriteIds(): number[] {
    try {
      const saved = JSON.parse(localStorage.getItem(ScoutviewComponent.IMAGE_FAVORITES_SELECTED_STORAGE_KEY) ?? '[]');
      return Array.isArray(saved) ? saved.map(Number).filter(id => !isNaN(id)) : [];
    } catch {
      return [];
    }
  }


  onImageFavoriteSelectionChange(): void {
    localStorage.setItem(ScoutviewComponent.IMAGE_FAVORITES_SELECTED_STORAGE_KEY, JSON.stringify(this.selectedImageFavoriteIds));
    if (!this.selectedImageFavoriteIds.includes(this.labelEditingImageFavoriteId)) {
      this.labelEditingImageFavoriteId = null;
    }
  }


  onImageFavoritesSizeChange(): void {
    localStorage.setItem(ScoutviewComponent.IMAGE_FAVORITES_LAYOUT_STORAGE_KEY, this.imageFavoritesSize);
  }


  canEditImageFavorite(imageFavorite: ImageFavoriteInfo): boolean {
    return imageFavorite.userFeatureAccess?.['EditImageFavorite'] === true;
  }


  canDeleteImageFavorite(imageFavorite: ImageFavoriteInfo): boolean {
    return imageFavorite.userFeatureAccess?.['DeleteImageFavorite'] === true;
  }


  imageFavoriteLabelsEditable(imageFavorite: ImageFavoriteInfo): boolean {
    return this.canEditImageFavorite(imageFavorite) &&
           (this.labelEditingImageFavoriteId == undefined || this.labelEditingImageFavoriteId === imageFavorite.id);
  }


  onImageFavoriteLabelsEditModeChange(imageFavorite: ImageFavoriteInfo, editing: boolean): void {
    if (editing) {
      this.labelEditingImageFavoriteId = imageFavorite.id;
    } else if (this.labelEditingImageFavoriteId === imageFavorite.id) {
      this.labelEditingImageFavoriteId = null;
    }
  }


  trackImageFavoriteById(_index: number, imageFavorite: ImageFavoriteInfo): number {
    return imageFavorite.id;
  }


  openImageFavoriteEditor(imageFavorite?: ImageFavoriteInfo): void {
    // Gateways with cameras the user can view supply dynamic backgrounds.
    const gateways: ImageFavoriteGateway[] = this.devices
      .filter(d => d.deviceCameras != undefined && d.deviceCameras.length > 0 && d.userFeatureAccess?.['ViewDevice'])
      .map(d => ({ serialNumber: d.serialNumber, name: d.name }));

    this.imageFavoriteEditRef = this.modalService.show(ImageFavoriteEditModalComponent, {
      animated: false,
      backdrop: 'static',
      initialState: {
        imageFavorite: imageFavorite ?? null,
        existingNames: this.imageFavorites.map(f => f.name),
        gateways,
        // 'Hour:' tags are built client-side (local hours) and aren't stored, so they can't match.
        tagSuggestions: [...new Set(this.imageTags.map(t => t.name).filter(n => !n.startsWith('Hour:')))].sort(),
        userGroups: this.associatedUserGroups,
        username: this.username
      }
    });
    const content = this.imageFavoriteEditRef.content as ImageFavoriteEditModalComponent;
    content.onSaved.subscribe((id: number) => this.getImageFavorites(id));
  }


  onDeleteImageFavoriteConfirm(imageFavorite: ImageFavoriteInfo): void {
    this.confirmationService.confirm({
      message: `Do you want to delete the image favorite "${imageFavorite.name}" and its labels?`,
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      key: 'cd1',
      accept: () => { this.deleteImageFavorite(imageFavorite); },
      reject: () => { }
    });
  }


  deleteImageFavorite(imageFavorite: ImageFavoriteInfo): void {
    this.imageFavoriteService.deleteImageFavorite(imageFavorite.id, this.username).subscribe(
      results => {
        this.imageFavorites = this.imageFavorites.filter(f => f.id !== imageFavorite.id);
        this.selectedImageFavoriteIds = this.selectedImageFavoriteIds.filter(id => id !== imageFavorite.id);
        this.onImageFavoriteSelectionChange();
        this.toastr.success('Image favorite deleted');
      },
      err => {
        this.toastr.error('Error unable to delete image favorite');
      });
  }

}



